/**
 * Click-to-place for Quick Chat / Voice from toolbar and explorer rail.
 * Right-click insert starts the composer immediately at the cursor instead.
 */

import { CANVAS_SNAP, snapToGrid } from "@/pages/canvas/canvas-controls.config";
import { useReactFlow } from "@xyflow/react";
import {
  type RefObject,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import { useCanvasUi } from "../context/CanvasUiContext";

export type DeferredQuickComposeMode = "quick-chat" | "quick-voice";

/** Matches ChatSessionNode bubble — constant 40×40 screen px via inverse zoom. */
export const QUICK_COMPOSE_GHOST_ICON_SIZE = 40;

export interface DeferredQuickComposeGhost {
  /** Snapped flow position of the bubble center (follows the cursor). */
  center: { x: number; y: number };
  mode: DeferredQuickComposeMode;
}

interface Position {
  x: number;
  y: number;
}

export interface UseDeferredQuickChatPlacementParams {
  lastCursorScreenPosRef: RefObject<Position | null>;
  onStartQuickChat: (screenPosition: { x: number; y: number }) => void;
  onStartQuickVoice: (screenPosition: { x: number; y: number }) => void;
}

export function useDeferredQuickChatPlacement({
  lastCursorScreenPosRef,
  onStartQuickChat,
  onStartQuickVoice,
}: UseDeferredQuickChatPlacementParams) {
  const { screenToFlowPosition } = useReactFlow();
  const { reactFlowWrapperRef } = useCanvasUi();
  const [pending, setPending] = useState<DeferredQuickComposeMode | null>(null);
  const [ghostPosition, setGhostPosition] = useState<Position | null>(null);

  const [snapX, snapY] = CANVAS_SNAP.grid;

  const snapFlowPoint = useCallback(
    (screenPos: Position): Position => {
      const flow = screenToFlowPosition(screenPos);
      return {
        x: snapToGrid(flow.x, snapX),
        y: snapToGrid(flow.y, snapY),
      };
    },
    [screenToFlowPosition, snapX, snapY],
  );

  const cancelPlacement = useCallback(() => {
    setPending(null);
    setGhostPosition(null);
  }, []);

  const beginPlacement = useCallback(
    (mode: DeferredQuickComposeMode) => {
      setPending(mode);
      const cursor = lastCursorScreenPosRef.current;
      setGhostPosition(cursor ? snapFlowPoint(cursor) : null);
    },
    [lastCursorScreenPosRef, snapFlowPoint],
  );

  const ghost = useMemo((): DeferredQuickComposeGhost | null => {
    if (!pending || !ghostPosition) return null;
    return { center: ghostPosition, mode: pending };
  }, [pending, ghostPosition]);

  useEffect(() => {
    if (!pending) return;

    const handler = (e: KeyboardEvent) => {
      if (e.key !== "Escape") return;
      e.preventDefault();
      cancelPlacement();
    };
    window.addEventListener("keydown", handler);
    return () => window.removeEventListener("keydown", handler);
  }, [pending, cancelPlacement]);

  useEffect(() => {
    if (!pending) return;

    const handler = (e: MouseEvent) => {
      const wrapper = reactFlowWrapperRef.current;
      if (wrapper && !wrapper.contains(e.target as globalThis.Node)) return;
      setGhostPosition(snapFlowPoint({ x: e.clientX, y: e.clientY }));
    };

    window.addEventListener("mousemove", handler);
    const cursor = lastCursorScreenPosRef.current;
    if (cursor) {
      setGhostPosition(snapFlowPoint(cursor));
    }

    return () => window.removeEventListener("mousemove", handler);
  }, [pending, reactFlowWrapperRef, lastCursorScreenPosRef, snapFlowPoint]);

  const commitAtScreen = useCallback(
    (clientX: number, clientY: number) => {
      if (!pending) return;
      // Chat session nodes anchor top-left; offset so the rust bubble center
      // lands on the click point (matches the centered ghost preview).
      const half = QUICK_COMPOSE_GHOST_ICON_SIZE / 2;
      const screenPos = { x: clientX - half, y: clientY - half };
      if (pending === "quick-chat") {
        onStartQuickChat(screenPos);
      } else {
        onStartQuickVoice(screenPos);
      }
      cancelPlacement();
    },
    [pending, onStartQuickChat, onStartQuickVoice, cancelPlacement],
  );

  const handlePaneClick = useCallback(
    (event: React.MouseEvent) => {
      if (!pending) return;
      commitAtScreen(event.clientX, event.clientY);
    },
    [pending, commitAtScreen],
  );

  return {
    isPlacing: pending != null,
    ghost,
    beginPlacement,
    cancelPlacement,
    handlePaneClick,
  };
}
